import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bell,
  BookOpen,
  CheckCircle2,
  Eye,
  Gift,
  HelpCircle,
  Images,
  Inbox,
  Instagram,
  MailCheck,
  MessageCircle,
  Send,
  Sparkles,
  Star,
  Stethoscope,
  Target,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";

// Card-tile home: KPI scorecards up top, every subpage one tap away.
const TILES = [
  { label: "Enquiries", desc: "Consultation requests & calendar", path: "/admin/enquiries", icon: Inbox, testid: "admin-tile-enquiries" },
  { label: "Leads CRM", desc: "Ad leads, pipeline & Meta sync", path: "/admin/leads", icon: Target, testid: "admin-tile-leads" },
  { label: "WhatsApp Inbox", desc: "Bot chats & staff replies", path: "/admin/whatsapp", icon: MessageCircle, testid: "admin-tile-whatsapp" },
  { label: "SEO AI Agent", desc: "Autopilot blog pipeline", path: "/admin/seo-agent", icon: Sparkles, testid: "admin-tile-seo-agent" },
  { label: "Ad Spy", desc: "Competitor FB/IG ads", path: "/admin/ad-spy", icon: Eye, testid: "admin-tile-ad-spy" },
  { label: "Journal", desc: "Articles & AI drafter", path: "/admin/journal", icon: BookOpen, testid: "admin-tile-journal" },
  { label: "SEO Metadata", desc: "Titles, SERP previews & tags", path: "/admin/seo", icon: TrendingUp, testid: "admin-tile-seo" },
  { label: "Treatments", desc: "Catalogue & pricing", path: "/admin/treatments", icon: Stethoscope, testid: "admin-tile-treatments" },
  { label: "Gallery", desc: "Before / after results", path: "/admin/gallery", icon: Images, testid: "admin-tile-gallery" },
  { label: "Testimonials", desc: "Patient reviews", path: "/admin/testimonials", icon: Star, testid: "admin-tile-testimonials" },
  { label: "FAQs", desc: "Patient questions", path: "/admin/faqs", icon: HelpCircle, testid: "admin-tile-faqs" },
  { label: "Instagram", desc: "Feed sync & picks", path: "/admin/instagram", icon: Instagram, testid: "admin-tile-instagram" },
];

export const OverviewTab = () => {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setRecent((r.data || []).slice(0, 5))).catch(() => {});
    api.get("/admin/connections").then((r) => setConnections(r.data || [])).catch(() => {});
  }, []);

  const cards = [
    { label: "Total Enquiries", value: stats?.enquiries_total, icon: Inbox, testid: "admin-stat-enquiries" },
    { label: "New Enquiries", value: stats?.enquiries_new, icon: Star, testid: "admin-stat-new" },
    { label: "Treatments", value: stats?.treatments, icon: Sparkles, testid: "admin-stat-treatments" },
    { label: "Doctors", value: stats?.doctors, icon: Stethoscope, testid: "admin-stat-doctors" },
    { label: "Push Subscribers", value: stats?.push_subscribers, icon: Bell, testid: "admin-stat-push-subscribers" },
    { label: "Pushes Delivered", value: stats?.push_sent, icon: Send, testid: "admin-stat-push-sent" },
    { label: "Reminders Sent", value: stats?.push_reminders, icon: MailCheck, testid: "admin-stat-push-reminders" },
    { label: "Welcome Offers Sent", value: stats?.push_welcomes, icon: Gift, testid: "admin-stat-push-welcomes" },
  ];

  return (
    <div className="space-y-10" data-testid="admin-overview-tab">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="glass rounded-3xl p-6" data-testid={c.testid}>
            <c.icon className="h-5 w-5 text-[#B38B2D]" aria-hidden="true" />
            <p className="mt-3 font-serif text-3xl font-semibold text-[#1A1615]">{c.value ?? "—"}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-[#A39288]">{c.label}</p>
          </div>
        ))}
      </div>
      {stats?.push_last_broadcast && (
        <p className="-mt-6 text-xs font-light text-[#A39288]" data-testid="admin-push-last-broadcast">
          Last push broadcast: {new Date(stats.push_last_broadcast).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
        </p>
      )}

      {connections.length > 0 && (
        <section className="glass rounded-3xl p-6" data-testid="admin-connections">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Connections at a glance</h2>
            <span className="rounded-full bg-[#F8F3E6] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a6d1f]" data-testid="admin-connections-score">
              {connections.filter((c) => c.status === "connected").length}/{connections.length} connected
            </span>
          </div>
          <p className="mt-1 text-xs font-light text-[#91837C]">Green = live and working. Amber = not set up yet — tap to fix it.</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {connections.map((c) => (
              <Link
                key={c.id}
                to={c.path}
                data-testid={`conn-${c.id}`}
                className="group flex items-start gap-3 rounded-xl border border-[#EFE7DF] bg-white/60 px-4 py-3 transition-colors hover:border-[#D4AF37]/50"
              >
                {c.status === "connected" ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-label="Connected" />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-label="Not set up" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-[#1A1615]">{c.label}</span>
                  <span className="block truncate text-[11px] font-light text-[#91837C]" title={c.detail}>{c.detail}</span>
                </span>
                <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#D4AF37] opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <section data-testid="admin-quick-tiles">
        <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Where to next?</h2>
        <p className="mt-1 text-xs font-light text-[#91837C]">Every corner of the clinic, one tap away — each opens its own page.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TILES.map((t) => (
            <Link
              key={t.path}
              to={t.path}
              data-testid={t.testid}
              className="group glass flex items-center gap-4 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(212,175,55,0.15)]"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F8F3E6] transition-colors duration-300 group-hover:bg-[#D4AF37]/20">
                <t.icon className="h-5 w-5 text-[#B38B2D]" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[#1A1615]">{t.label}</span>
                <span className="block truncate text-xs font-light text-[#91837C]">{t.desc}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-[#D4AF37] opacity-0 transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Latest Enquiries</h2>
          <Link to="/admin/enquiries" data-testid="admin-view-all-enquiries-button" className="text-xs font-medium text-[#B38B2D] hover:underline">
            View all →
          </Link>
        </div>
        <div className="mt-4 space-y-3" data-testid="admin-recent-enquiries">
          {recent.length === 0 && (
            <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">No enquiries yet — they will appear here the moment a patient reaches out.</p>
          )}
          {recent.map((e) => (
            <div key={e.id} className="glass flex flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-4">
              <div>
                <p className="text-sm font-semibold text-[#1A1615]">{e.name}</p>
                <p className="text-xs text-[#91837C]">{e.treatment || "General enquiry"} · {e.email}</p>
              </div>
              <span className="rounded-full bg-[#D4AF37]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a6d1f]">
                {e.status}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
